/**
 * 放送コンテンツ編集 UI の純関数ロジック。
 *
 * 分割前は `components/BroadcastContentsEditor.tsx` にコンポーネントと同居していた
 * 純関数を集約した (`react-refresh/only-export-components` 対策も兼ねる)。
 * ロジックは一切変更しておらず、そのまま移設したのみ。
 *
 * ドメイン (データモデル) 寄りのデフォルト値・バリデーションは
 * `utils/broadcastContents.ts` 側に既にあるため、こちらでは重複実装せず import して使う。
 */
import type {
  BroadcastCondition,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  FreeTextInputMode,
  FreeTextProperties,
} from '../../api/types';
import { GRANULARITY_DEFAULT_SSML, isEmptySsml } from '../../utils/broadcastContents';
import { PLAYBACK_RATE_MAX, PLAYBACK_RATE_MIN } from './constants';

/**
 * 数値が有限で範囲内かどうかを判定する簡易バリデーション。
 * `NaN` / `Infinity` / 範囲外はすべて false を返す。
 */
export function isValidPlaybackRate(value: number): boolean {
  return Number.isFinite(value) && value >= PLAYBACK_RATE_MIN && value <= PLAYBACK_RATE_MAX;
}

/**
 * 自列車遅延パターンの新規行デフォルト値を生成する (「＋」追加時に使用)。
 *
 * SSML 入力欄に粒度別デフォルト文 (`GRANULARITY_DEFAULT_SSML`) を **実値として**
 * 入れる方針に変更したため、新規行追加時にも対応する粒度のデフォルト文を
 * `ssml_template` に詰める。`templates` を切り替えれば接続線 transfer_aware 用の
 * デフォルト文 (`CONNECTING_GRANULARITY_DEFAULT_SSML`) にも適用できる。
 */
export function createDefaultPattern(
  templates: Record<DelayBroadcastGranularity, string> = GRANULARITY_DEFAULT_SSML,
): DelayBroadcastPattern {
  return {
    delay_minutes_from: 0,
    granularity: '1分単位',
    ssml_template: templates['1分単位'],
  };
}

/**
 * 既存の properties から入力モードを推測する。
 * - `input_mode` が明示されていればそれを使う。
 * - `audio_key` があれば audio、中身のある `ssml` があれば ssml、それ以外は text。
 *
 * 「通常テキストをデフォルトにする」仕様変更 (Issue #340) にあわせ、
 * 中身のない SSML (旧初期値の `<speak></speak>` など) は「SSML 未設定」として扱い、
 * 通常テキストモードにフォールバックする。これにより旧仕様で作成された
 * レコードを開いたときにも、意図せず SSML モードで開くことがなくなる。
 */
export function resolveInputMode(props: Partial<FreeTextProperties>): FreeTextInputMode {
  if (props.input_mode) return props.input_mode;
  if (props.audio_key && props.audio_key.trim() !== '') return 'audio';
  if (!isEmptySsml(props.ssml)) return 'ssml';
  return 'text';
}

/**
 * `conditions[index]` に `patch` をマージした新配列を返す (immutable)。
 *
 * `BroadcastCondition` は `condition_type` を discriminant とする判別可能ユニオン
 * (接続線遅延 / 終着番線) になったため、`Partial<BroadcastCondition>` を受けると
 * 「どちらのメンバの部分集合か」が失われ、マージ結果の型が壊れてしまう。
 * そこで条件の具体型 `T` でジェネリック化し、**同一メンバ内の部分更新** だけを
 * 許可する (種別をまたぐ差し替えは patch ではなく `onReplace` 相当で行う)。
 */
export function patchCondition<T extends BroadcastCondition>(
  conditions: T[],
  index: number,
  patch: Partial<T>,
): T[] {
  return conditions.map((c, i) => (i === index ? { ...c, ...patch } : c));
}

/**
 * 配列から `index` 番目を除去した新配列を返す。
 */
export function removeAt<T>(arr: T[], index: number): T[] {
  return arr.filter((_, i) => i !== index);
}
